/**
 * Shared File-Upload Validator + useDropZone hook
 * ------------------------------------------------
 * Created Feb 22, 2026 after the Clarification Received drag-drop bug
 * exposed duplicated, inconsistent upload logic across ~6 components.
 *
 * Goals:
 *   1. One canonical `validateFile(file, options)` so size / type rules
 *      never drift between components.
 *   2. One `useDropZone(options)` hook that wires click-to-upload AND
 *      drag-drop to IDENTICAL code paths, eliminating the entire bug
 *      class of "click works but drag doesn't."
 *   3. Return a standardized contract every consumer can spread onto
 *      their DOM — so new upload fields can't accidentally forget
 *      a handler.
 *
 * Permissions MUST be enforced at the component-render level (or API
 * level), not inside any handler in this module.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

// --------------------------------------------------------------------------
// Canonical defaults
// --------------------------------------------------------------------------
export const DEFAULT_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const DEFAULT_ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];

/**
 * Convert bytes to a short human label (e.g., 10485760 → "10MB").
 */
export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return `${Math.round(mb)}MB`;
  const kb = bytes / 1024;
  return `${Math.round(kb)}KB`;
};

/**
 * Canonical per-file validator.
 *
 * Returns `{ ok: true, file }` on success or `{ ok: false, error }` on any
 * rejection — never throws. Error messages are user-facing and consistent
 * across the app.
 *
 * Options:
 *   allowedTypes        — array of MIME strings (default PDF/JPG/PNG)
 *   allowedExtensions   — optional extension whitelist (use when MIME
 *                         may be empty, e.g. some Outlook .msg cases)
 *   maxSize             — bytes (default 10 MB)
 */
export function validateFile(file, options = {}) {
  const {
    allowedTypes = DEFAULT_ALLOWED_TYPES,
    allowedExtensions = null,
    maxSize = DEFAULT_MAX_SIZE_BYTES,
  } = options;

  if (!file) return { ok: false, error: null };

  const mime = (file.type || '').toLowerCase();
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';

  const mimeOk = allowedTypes.includes(mime);
  const extOk = allowedExtensions
    ? allowedExtensions.includes(ext)
    : false;

  if (!mimeOk && !extOk) {
    const human = allowedTypes
      .map((t) => t.replace('application/', '').replace('image/', '').toUpperCase())
      .join(', ');
    return { ok: false, error: `Only ${human} files are allowed` };
  }

  if (typeof maxSize === 'number' && file.size > maxSize) {
    return {
      ok: false,
      error: `File size must be less than ${formatBytes(maxSize)}`,
    };
  }

  return { ok: true, file };
}

// --------------------------------------------------------------------------
// useDropZone hook
// --------------------------------------------------------------------------
/**
 * Wires a dropzone element and a hidden file input to a single, consistent
 * click+drag pipeline.
 *
 * Usage:
 *   const zone = useDropZone({
 *     allowedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
 *     maxSize: 10 * 1024 * 1024,
 *     onFileAccepted: (file) => setResponseFile(file),
 *   });
 *
 *   <label {...zone.dropzoneProps}>
 *     <input {...zone.inputProps} />
 *     {zone.isDragging ? 'Drop here' : 'Click to upload'}
 *   </label>
 *   {zone.error && <p className="text-red-600">{zone.error}</p>}
 *
 * Return contract (per product spec Feb 22, 2026):
 *   { isDragging, file, error, onDrop, onClick, inputProps, reset, dropzoneProps }
 *   — `dropzoneProps` is a convenience bag (spreadable) containing
 *     onDragEnter / onDragOver / onDragLeave / onDrop / onClick.
 */
export function useDropZone(options = {}) {
  const {
    allowedTypes = DEFAULT_ALLOWED_TYPES,
    allowedExtensions = null,
    maxSize = DEFAULT_MAX_SIZE_BYTES,
    multiple = false,
    disabled = false,
    onFileAccepted = null,
    onFilesAccepted = null, // for multiple=true
    // Feb 2026 — Multi-file Received-Document upload support.
    // When `appendMode` is true (only meaningful with multiple=true),
    // each successful drop/select APPENDS the new files to the
    // existing list instead of replacing it. Cap enforced via
    // `maxFiles` (file count) and `maxTotalSize` (sum of bytes).
    appendMode = false,
    maxFiles = null,        // null = unlimited
    maxTotalSize = null,    // null = unlimited (bytes)
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(appendMode ? [] : null); // single file OR array when multiple
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const reset = useCallback(() => {
    setFile(appendMode ? [] : null);
    setError(null);
    setIsDragging(false);
    if (inputRef.current) inputRef.current.value = '';
  }, [appendMode]);

  // Feb 2026 — remove one file from the accumulated list (multi-file mode).
  // Index-based for stable React-key removal even when two dropped files
  // share the same name.
  const removeAt = useCallback((index) => {
    if (!appendMode) return;
    setError(null);
    setFile((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, [appendMode]);

  const processFileList = useCallback(
    (fileList) => {
      if (disabled || !fileList || fileList.length === 0) return;
      const list = Array.from(fileList);

      if (!multiple && list.length > 1) {
        setError('Please drop only one file at a time');
        return;
      }

      // Validate each
      const validated = [];
      for (const f of list) {
        const r = validateFile(f, { allowedTypes, allowedExtensions, maxSize });
        if (!r.ok) {
          setError(r.error || 'Invalid file');
          return; // fail-closed on first error, matching prior per-component UX
        }
        validated.push(r.file);
      }

      // Feb 2026 — Append-mode caps (multi-file Received-Document flow).
      if (appendMode) {
        setFile((prev) => {
          const current = Array.isArray(prev) ? prev : [];
          const merged = [...current, ...validated];
          if (maxFiles != null && merged.length > maxFiles) {
            setError(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed per submission.`);
            return current;
          }
          if (maxTotalSize != null) {
            const total = merged.reduce((sum, f) => sum + (f.size || 0), 0);
            if (total > maxTotalSize) {
              setError(`Total upload size exceeds ${formatBytes(maxTotalSize)} limit.`);
              return current;
            }
          }
          setError(null);
          if (onFilesAccepted) onFilesAccepted(merged);
          return merged;
        });
        return;
      }

      setError(null);
      if (multiple) {
        setFile(validated);
        if (onFilesAccepted) onFilesAccepted(validated);
      } else {
        setFile(validated[0]);
        if (onFileAccepted) onFileAccepted(validated[0]);
      }
    },
    [disabled, multiple, allowedTypes, allowedExtensions, maxSize,
     onFileAccepted, onFilesAccepted,
     appendMode, maxFiles, maxTotalSize]
  );

  // ---------- click-to-upload ----------
  const handleClick = useCallback(() => {
    if (disabled) return;
    if (inputRef.current) inputRef.current.click();
  }, [disabled]);

  const handleInputChange = useCallback(
    (e) => {
      processFileList(e.target.files);
      // Reset input so selecting the same file again re-triggers onChange.
      if (e.target) e.target.value = '';
    },
    [processFileList]
  );

  // ---------- drag-and-drop ----------
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(true);
  }, [disabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setIsDragging((prev) => prev || true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    // Ignore bubble-outs from child elements.
    if (e.currentTarget && e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  }, [disabled]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;
      processFileList(e.dataTransfer?.files);
    },
    [disabled, processFileList]
  );

  // Stable memoized prop bundles so consumers can spread directly.
  const dropzoneProps = useMemo(
    () => ({
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      onClick: handleClick,
    }),
    [handleDragEnter, handleDragOver, handleDragLeave, handleDrop, handleClick]
  );

  const inputProps = useMemo(
    () => ({
      ref: inputRef,
      type: 'file',
      multiple,
      accept: buildAcceptAttribute(allowedTypes, allowedExtensions),
      onChange: handleInputChange,
      style: { display: 'none' },
      'aria-hidden': true,
      tabIndex: -1,
      disabled,
    }),
    [multiple, allowedTypes, allowedExtensions, disabled, handleInputChange]
  );

  return {
    isDragging,
    file,
    error,
    setError,      // bonus: lets consumers add their own error (e.g., API failure)
    onDrop: handleDrop,
    onClick: handleClick,
    inputProps,
    dropzoneProps, // convenience bag for spread
    reset,
    removeAt,      // Feb 2026 — append-mode helper (multi-file Received uploads)
  };
}

// --------------------------------------------------------------------------
// Internal helpers
// --------------------------------------------------------------------------
function buildAcceptAttribute(allowedTypes, allowedExtensions) {
  const parts = [...(allowedTypes || [])];
  if (allowedExtensions) {
    for (const ext of allowedExtensions) {
      parts.push(ext.startsWith('.') ? ext : `.${ext}`);
    }
  }
  return parts.join(',');
}
